import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import NotificationsModal from '../notifications/NotificationsModal';
import ProfileAvatar from '../profile/ProfileAvatar';
import { API_CONFIG } from '../../config/apiConfig';
import { getProfilePictureUrl } from '../../utils/imageUtils';
import { canAccessAnalytics } from '../../utils/accessControl';
import {
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  AcademicCapIcon,
  HomeIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  BriefcaseIcon,
  CogIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import './Navbar.css';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const {
    hasNewAnnouncements,
    getTotalUnreadCount,
    isNotificationsModalOpen,
    openNotificationsModal,
    closeNotificationsModal
  } = useAnnouncements();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Check if user has admin privileges
  const isAdmin = user && (user.is_staff || user.is_superuser || user.role === 'admin');
  const canUseAnalytics = canAccessAnalytics(user);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  // Main navigation items - visible to all users
  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about', icon: UserIcon },
    { name: 'Courses', href: '/courses', icon: AcademicCapIcon },
    { name: 'Opportunities', href: '/opportunities', icon: BriefcaseIcon },
    { name: 'Career Guidance', href: '/career', icon: LightBulbIcon },
  ];

  // User-specific navigation items - only visible when authenticated
  const userNavigation = isAuthenticated ? [
    { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
    { name: 'Profile', href: '/profile', icon: UserIcon },
  ] : [];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          {/* Logo Section */}
          <div className="navbar-logo">
            <Link to="/" className="logo-link" onClick={closeMobileMenu}>
              <div className="logo-icon">
                <img
                  src="/images/GPG_LOGO.png"
                  alt="Gauteng Provincial Government"
                  className="logo-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="logo-fallback">G</div>
              </div>
              <div className="logo-text">
                <div className="brand-name">GMSP3</div>
                <div className="brand-subtitle">Career Guidance</div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul className="navbar-nav">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.name} className="nav-item">
                  <Link
                    to={item.href}
                    className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                  >
                    <Icon className="nav-icon" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* User Menu */}
          <div className="user-menu">
            {isAuthenticated ? (
              <div className="user-nav">
                {/* Dashboard Link */}
                <Link
                  to="/dashboard"
                  className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                >
                  <ChartBarIcon className="nav-icon" />
                  Dashboard
                </Link>

                {/* Analytics Link - Privileged Users */}
                {canUseAnalytics && (
                  <Link
                    to="/analytics"
                    className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
                    title="Analytics Dashboard"
                  >
                    <ChartBarIcon className="nav-icon" />
                    Analytics
                  </Link>
                )}

                {/* Notifications Button */}
                <button
                  onClick={openNotificationsModal}
                  className="icon-button"
                  title="Notifications"
                >
                  <BellIcon className="icon" />
                  {hasNewAnnouncements() && (
                    <span className="notification-badge">
                      {getTotalUnreadCount ? getTotalUnreadCount() : '!'}
                    </span>
                  )}
                </button>

                {/* User Avatar Dropdown */}
                <div className="user-dropdown" ref={dropdownRef}>
                  <button
                    onClick={toggleUserDropdown}
                    className="user-avatar-button"
                    title="User Menu"
                  >
                    <ProfileAvatar
                      src={user?.profile_picture_url || getProfilePictureUrl(user?.profile_picture)}
                      alt={`${user?.first_name} ${user?.last_name}`}
                      size="small"
                      showVerifiedBadge={true}
                      isVerified={user?.is_verified}
                    />
                    <ChevronDownIcon className="dropdown-arrow" />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserDropdownOpen && (
                    <div className="user-dropdown-menu">
                      <div className="dropdown-header">
                        <ProfileAvatar
                          src={user?.profile_picture_url || getProfilePictureUrl(user?.profile_picture)}
                          alt={`${user?.first_name} ${user?.last_name}`}
                          size="medium"
                          showVerifiedBadge={true}
                          isVerified={user?.is_verified}
                        />
                        <div className="user-info">
                          <div className="user-name">{user?.first_name} {user?.last_name}</div>
                          <div className="user-email">{user?.email}</div>
                        </div>
                      </div>

                      <div className="dropdown-divider"></div>

                      <Link
                        to="/profile"
                        className="dropdown-item"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <UserIcon className="dropdown-icon" />
                        Profile
                      </Link>

                  {isAdmin && (
                    <a
                      href={API_CONFIG.admin.djangoAdmin()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-item"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <CogIcon className="dropdown-icon" />
                          Admin
                        </a>
                      )}

                      <Link
                        to="/help"
                        className="dropdown-item"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <QuestionMarkCircleIcon className="dropdown-icon" />
                        Help
                      </Link>

                      <div className="dropdown-divider"></div>

                      <button
                        onClick={handleLogout}
                        className="dropdown-item logout-item"
                      >
                        <ArrowRightOnRectangleIcon className="dropdown-icon" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="btn-login">
                  Login
                </Link>
                <Link to="/register" className="btn-register">
                  Register
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-button"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="icon" />
              ) : (
                <Bars3Icon className="icon" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu">
            {/* Main Navigation */}
            <nav className="mobile-nav">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <Icon className="nav-icon" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            {isAuthenticated ? (
              <div className="mobile-user-section">
                {/* User Profile Header */}
                <div className="mobile-user-header">
                  <ProfileAvatar
                    src={user?.profile_picture_url || getProfilePictureUrl(user?.profile_picture)}
                    alt={`${user?.first_name} ${user?.last_name}`}
                    size="medium"
                    showVerifiedBadge={true}
                    isVerified={user?.is_verified}
                  />
                  <div className="mobile-user-info">
                    <div className="mobile-user-name">{user?.first_name} {user?.last_name}</div>
                    <div className="mobile-user-email">{user?.email}</div>
                  </div>
                </div>

                <nav className="mobile-user-nav">
                  <Link
                    to="/dashboard"
                    className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <ChartBarIcon className="nav-icon" />
                    Dashboard
                  </Link>

                  <Link
                    to="/profile"
                    className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <UserIcon className="nav-icon" />
                    Profile
                  </Link>

                  {/* Analytics Link - Admin Only */}
                  {isAdmin && (
                    <Link
                      to="/analytics"
                      className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
                      onClick={closeMobileMenu}
                    >
                      <ChartBarIcon className="nav-icon" />
                      Analytics
                    </Link>
                  )}

                  {/* Django Admin Link - Admin Only */}
                  {isAdmin && (
                    <a
                      href={API_CONFIG.admin.djangoAdmin()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link"
                      onClick={closeMobileMenu}
                    >
                      <CogIcon className="nav-icon" />
                      Admin
                    </a>
                  )}

                  <Link
                    to="/help"
                    className="nav-link"
                    onClick={closeMobileMenu}
                  >
                    <QuestionMarkCircleIcon className="nav-icon" />
                    Help
                  </Link>

                  <button
                    onClick={() => {
                      openNotificationsModal();
                      closeMobileMenu();
                    }}
                    className="nav-link"
                    style={{ border: 'none', background: 'none', textAlign: 'left' }}
                  >
                    <BellIcon className="nav-icon" />
                    Notifications
                    {hasNewAnnouncements() && (
                      <span className="notification-badge">
                        {getTotalUnreadCount ? getTotalUnreadCount() : '!'}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={handleLogout}
                    className="nav-link logout-item"
                    style={{ border: 'none', background: 'none', textAlign: 'left' }}
                  >
                    <ArrowRightOnRectangleIcon className="nav-icon" />
                    Logout
                  </button>
                </nav>
              </div>
            ) : (
              <div className="mobile-user-section">
                <div className="auth-buttons">
                  <Link to="/login" className="btn-login" onClick={closeMobileMenu}>
                    Login
                  </Link>
                  <Link to="/register" className="btn-register" onClick={closeMobileMenu}>
                    Register
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Notifications Modal */}
      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={closeNotificationsModal}
      />
    </>
  );
};

export default Navbar;
